import { useIsomorphicLayoutEffect } from '@cian/react-utils';
import { Buttons, Image, Overlay, Scroller, Slider } from '@cian/slider';
import cx from 'clsx';
import * as React from 'react';
import { trackImageClick } from 'shared/containers/NewbuildingGalleryContainer/tracking';
import { IHotkey, useHotkeys } from 'shared/hooks/useHotkeys';

import * as styles from './GalleryInner.css';
import { ButtonFullscreen } from './components/ButtonFullscreen';
import { SlideActiveObserver } from './components/SlideActiveObserver';
import { MAP_TYPE_TO_PLAYERS } from './constants';
import { trackLayoutSlideView } from '../../tracking';
import { EMediaType, TMedia } from '../../types';
import { CianLayout } from '../CianLayout';
import { PaginationThumbs } from '../PaginationThumbs';
import { VideoPlayable } from '../VideoPlayable';

interface IGalleryInnerSlots {
  slotScrollerEnd?: React.ReactNode;
}

interface IGalleryInnerProps extends IGalleryInnerSlots {
  autofocus?: boolean;
  media: TMedia[];
  bordered?: boolean;
  fullscreenable?: boolean;
  keyboard?: boolean;
  objectFit?: React.ComponentProps<typeof Image>['objectFit'];
  thumbsBounded?: boolean;
  videoPlayable?: boolean;
  withBackgroundBlur?: boolean;
  currentSlide: number;
  onSlideChange?(index: number): void;
  onScrollerClick?(): void;
  onFullscreenClick?(): void;
  ref?: React.Ref<React.ComponentRef<typeof Slider>>;
}

const GalleryInnerComponent = function GalleryInner({
  autofocus,
  media,
  bordered,
  fullscreenable,
  keyboard = true,
  objectFit,
  thumbsBounded,
  videoPlayable,
  slotScrollerEnd,
  withBackgroundBlur,
  currentSlide,
  onSlideChange,
  onScrollerClick,
  onFullscreenClick,
  ref,
}: IGalleryInnerProps) {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const sliderRef = React.useRef<React.ComponentRef<typeof Slider>>(null);
  const paginationThumbsRef = React.useRef<React.ComponentRef<typeof PaginationThumbs>>(null);

  const handleScrollEnd = React.useCallback(() => {
    paginationThumbsRef.current?.scrollCurrentThumbIntoView();
  }, []);

  const handleScrollerClick = React.useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      const target = event.target as HTMLElement;
      if (target?.closest('a, button')) {
        return;
      }

      if (onScrollerClick) {
        onScrollerClick();
      }
    },
    [onScrollerClick],
  );

  const hotkeys = React.useMemo<IHotkey[]>(
    () => [
      {
        keyCode: 'ArrowLeft',
        onKeyDown: () => sliderRef.current?.scrollPrev(),
        disabled: !keyboard,
      },
      {
        keyCode: 'ArrowRight',
        onKeyDown: () => sliderRef.current?.scrollNext(),
        disabled: !keyboard,
      },
    ],
    [keyboard],
  );
  const { onKeyDown } = useHotkeys(hotkeys);

  React.useImperativeHandle<React.ComponentRef<typeof Slider> | null, React.ComponentRef<typeof Slider> | null>(
    ref,
    () => sliderRef.current,
    [],
  );

  useIsomorphicLayoutEffect(() => {
    /* istanbul ignore next */
    if (autofocus) {
      requestAnimationFrame(() => {
        containerRef.current?.focus({ preventScroll: true });
      });
    }
  });

  return (
    <div
      className={styles['container']}
      data-name="GalleryInnerComponent"
      tabIndex={0}
      onKeyDown={onKeyDown}
      ref={containerRef}
    >
      <Slider ref={sliderRef} loop onSlideChange={onSlideChange} behavior="auto">
        <div
          className={cx(styles['scroller'], { [styles['scroller--bordered']]: bordered })}
          onClick={handleScrollerClick}
        >
          <Scroller onScrollEnd={handleScrollEnd}>
            {media.map((m, index) => {
              switch (m.type) {
                case EMediaType.Layout: {
                  return (
                    <SlideActiveObserver callback={trackLayoutSlideView} skip={!fullscreenable} key={`slide_${index}`}>
                      <Image
                        src={m.src}
                        objectFit="contain"
                        loading={index === 0 ? null : undefined}
                        decoding={index === 0 ? null : undefined}
                        blurBackground={false}
                        priority={index === 0}
                        onClick={trackImageClick}
                      />
                    </SlideActiveObserver>
                  );
                }
                case EMediaType.Photo: {
                  return (
                    <Image
                      key={`slide_${index}`}
                      src={m.src}
                      objectFit={objectFit || 'cover'}
                      loading={index === 0 ? null : undefined}
                      decoding={index === 0 ? null : undefined}
                      blurBackground={withBackgroundBlur}
                      priority={index === 0}
                      onClick={trackImageClick}
                    />
                  );
                }
                case EMediaType.Video:
                case EMediaType.VideoVk:
                case EMediaType.VideoRutube: {
                  const Player = MAP_TYPE_TO_PLAYERS[m.type];

                  return (
                    <VideoPlayable
                      key={`slide_${index}`}
                      playable={videoPlayable}
                      src={m.src}
                      previewUrl={m.previewUrl}
                      player={<Player {...m} />}
                      withBackgroundBlur={withBackgroundBlur}
                      backgroundSize="contain"
                      isActive={currentSlide === index}
                      videoSource={m.type}
                    />
                  );
                }
                case EMediaType.CianLayout:
                  return (
                    <SlideActiveObserver callback={trackLayoutSlideView} skip={!fullscreenable} key={`slide_${index}`}>
                      <CianLayout src={m.src} deoptimised={index === 0} />
                    </SlideActiveObserver>
                  );

                case EMediaType.Component:
                  return m.component;
                case EMediaType.Tour:
                  return m.component;
                case EMediaType.FloorPlan:
                  return m.component;
              }
            })}
          </Scroller>
          {(fullscreenable || media.length > 1) && (
            <Overlay>
              {fullscreenable && <ButtonFullscreen onClick={onFullscreenClick} />}
              {media.length > 1 && <Buttons size="M" />}
            </Overlay>
          )}
          {slotScrollerEnd}
        </div>
        <div className={cx(styles['thumbs-wrapper'], { [styles['thumbs-wrapper--bounded']]: thumbsBounded })}>
          <PaginationThumbs ref={paginationThumbsRef} media={media} />
        </div>
      </Slider>
    </div>
  );
};

export const GalleryInner = React.memo(GalleryInnerComponent);
